// Copyright (c) Microsoft. All rights reserved.

import { FC, useEffect, useState, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../../redux/app/hooks';
import { RootState } from '../../../redux/app/store';
import { useChat } from '../../../libs/hooks/useChat_v2';
import { showAlertToast } from '../../../redux/features/app/appSlice';
import { AlertType } from '../../../libs/models/AlertType';
import classNames from 'classnames';

import { ChatListContainer } from './ChatListContainer';
import { ChatSet } from './ChatSet';
import { Button } from '../../../components/Button';
import { Tooltip } from '../../../components/Tooltip';
import { ChatSettingDialog } from './dialogs/ChatSettingDialog';
import { ChatFeedbackDialog } from './dialogs/ChatFeedbackDialog';


interface ChatAsideProps {
  isAsideCollapsed: boolean;
  toggleAside: () => void;
}

export const ChatAside: FC<ChatAsideProps> = ({ isAsideCollapsed, toggleAside }) => {
  const chat = useChat();
  const dispatch = useAppDispatch();
  const scrollViewTargetRef = useRef<HTMLDivElement>(null);
  const { conversations, continuationToken } = useAppSelector((state: RootState) => state.conversations_v2);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setHasMore(continuationToken !== null);
    setIsLoading(false);
  }, [continuationToken]);

  const onMoreChatRoom = () => {
    try {
      setIsLoading(true);
      if (hasMore) {
        chat
          .loadChats()
          .then()
          .catch(() => {
            setHasMore(false);
            setIsLoading(false);
            const message = `이전 채팅방을 불러오는 중에 문제가 발생했어요. 상단 새로고침 버튼을 눌러주세요.`;
            dispatch(
              showAlertToast({
                altMessage: message,
                altType: AlertType.Info,
              }),
            );
          });
      }
    } catch {}
  };

  return (
    <aside className={classNames('chat-aside', { 'is-close': isAsideCollapsed })}>
      <ChatSet />

      <ChatListContainer
        conversations={conversations}
        hasMore={hasMore}
        onLoadMore={onMoreChatRoom}
        isLoading={isLoading}
        scrollViewTargetRef={scrollViewTargetRef}
      />
      <Tooltip
        className={classNames('chat-aside-collapse', { 'is-close': isAsideCollapsed })}
        message={isAsideCollapsed ? '사이드바 열기' : '사이드바 닫기'}
        theme="inverse"
        position="right"
      >
        <Button
          className="collapse-control"
          control
          textBlind
          suffix={<span className="move-arrow"></span>}
          onClick={toggleAside}
        >
          {isAsideCollapsed ? '사이드바 열기' : '사이드바 닫기'}
        </Button>
      </Tooltip>
      
      {/* 사용량 */}
      {/* <UsageContainer /> */}
      {/* 설정 */}
      <div className='aside-footer'>
        <ChatSettingDialog/>
        <ChatFeedbackDialog/>
      </div>
      
    </aside>
  );
};
